import { useState, useEffect } from 'react';
import { 
  Wallet, CheckCircle, XCircle, Clock, Search, Filter,
  Building2, User, Calendar, Loader2, ArrowDownLeft, Eye, CalendarCog
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { useRegion } from '../context/RegionContext';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminWithdrawals() {
  const { language } = useLanguage();
  const { isDarkMode } = useTheme();
  const { formatPrice } = useRegion();
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [processingId, setProcessingId] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);

  useEffect(() => {
    fetchWithdrawalRequests();
  }, []);

  const fetchWithdrawalRequests = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('business_token');
      const response = await axios.get(`${BACKEND_URL}/api/admin/withdrawal-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRequests(response.data || []);
    } catch (error) {
      console.error('Error fetching requests:', error);
      if (error.response?.status === 403) {
        toast.error(language === 'ar' ? 'ليس لديك صلاحية الوصول' : 'Access denied');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleProcessRequest = async (requestId, action) => {
    try {
      setProcessingId(requestId);
      const token = localStorage.getItem('business_token');
      
      await axios.put(
        `${BACKEND_URL}/api/admin/withdrawal-requests/${requestId}?action=${action}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(
        action === 'approve'
          ? (language === 'ar' ? 'تمت الموافقة على الطلب' : 'Request approved')
          : (language === 'ar' ? 'تم رفض الطلب' : 'Request rejected')
      );
      
      fetchWithdrawalRequests();
      setSelectedRequest(null);
    } catch (error) {
      toast.error(language === 'ar' ? 'حدث خطأ' : 'An error occurred');
    } finally {
      setProcessingId(null);
    }
  };

  // iter-224: Admin can back-date a withdrawal for record correction.
  // Accepts the reference number (WDB-YYYY-XXXXXXX) or MongoDB _id.
  const handleEditDate = async (request) => {
    const ref = request.reference_number || request.referenceNumber || request.id || request._id;
    const currentIso = request.createdAt ? new Date(request.createdAt).toISOString().slice(0, 10) : '';
    const promptMsg = language === 'ar'
      ? `تعديل تاريخ الطلب ${ref}\nأدخل التاريخ الجديد بصيغة YYYY-MM-DD (مثال: 2026-07-29):`
      : `Edit date for ${ref}\nEnter new date as YYYY-MM-DD (e.g. 2026-07-29):`;
    const input = window.prompt(promptMsg, currentIso);
    if (!input) return;
    const trimmed = input.trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      toast.error(language === 'ar' ? 'صيغة تاريخ غير صحيحة' : 'Invalid date format');
      return;
    }
    try {
      setProcessingId(request.id || request._id);
      const token = localStorage.getItem('business_token');
      const res = await axios.patch(
        `${BACKEND_URL}/api/admin/withdrawals/${encodeURIComponent(ref)}/date`,
        { date: trimmed },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(
        language === 'ar'
          ? `تم تعديل التاريخ إلى ${trimmed}`
          : `Date updated to ${trimmed} (${res.data.updated_transactions || 0} tx synced)`
      );
      fetchWithdrawalRequests();
    } catch (error) {
      const msg = error.response?.data?.detail || (language === 'ar' ? 'فشل تعديل التاريخ' : 'Failed to update date');
      toast.error(msg);
    } finally {
      setProcessingId(null);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      completed: {
        bg: isDarkMode ? 'bg-green-500/20' : 'bg-green-100',
        text: 'text-green-500',
        icon: CheckCircle,
        label: language === 'ar' ? 'مكتمل' : 'Completed'
      },
      pending: {
        bg: isDarkMode ? 'bg-amber-500/20' : 'bg-amber-100',
        text: 'text-amber-500',
        icon: Clock,
        label: language === 'ar' ? 'قيد الانتظار' : 'Pending'
      },
      failed: {
        bg: isDarkMode ? 'bg-red-500/20' : 'bg-red-100',
        text: 'text-red-500',
        icon: XCircle,
        label: language === 'ar' ? 'مرفوض' : 'Rejected'
      }
    };
    const style = styles[status] || styles.pending;
    const Icon = style.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${style.bg} ${style.text}`}>
        <Icon className="h-3 w-3" />
        {style.label}
      </span>
    );
  };

  const filteredRequests = requests.filter(req => {
    const matchesFilter = filter === 'all' || req.status === filter;
    const matchesSearch = !searchQuery || 
      req.userName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      req.userEmail?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const stats = {
    total: requests.length,
    pending: requests.filter(r => r.status === 'pending').length,
    completed: requests.filter(r => r.status === 'completed').length,
    rejected: requests.filter(r => r.status === 'failed').length,
    totalAmount: requests.filter(r => r.status === 'pending').reduce((sum, r) => sum + (r.amount || 0), 0)
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center cgm-page`}>
        <div className="relative">
          <div className="w-16 h-16 rounded-full border-4 border-[#38BDF8]/20 border-t-[#38BDF8] animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen py-6 px-4 cgm-page`}>
      {/* Background Effects */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className={`absolute top-20 start-10 w-[300px] h-[300px] rounded-full blur-[100px] ${isDarkMode ? 'bg-[#38BDF8]/10' : 'bg-[#38BDF8]/5'}`} />
        <div className={`absolute bottom-20 end-10 w-[250px] h-[250px] rounded-full blur-[80px] ${isDarkMode ? 'bg-[#F97316]/10' : 'bg-[#F97316]/5'}`} />
      </div>
      
      <div className="relative max-w-6xl mx-auto">
        {/* Header */}
        <div className={`mb-5 p-4 rounded-xl ${
          isDarkMode ? 'bg-[#131B2C]/80 border border-white/10' : 'bg-white/80 backdrop-blur-xl border border-black/5 shadow-sm'
        }`}>
          <h1 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
            <span className="text-gradient">{language === 'ar' ? 'طلبات السحب' : 'Withdrawal Requests'}</span>
          </h1>
          <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {language === 'ar' ? 'إدارة ومراجعة طلبات سحب الأرصدة' : 'Manage and review balance withdrawal requests'}
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
          <div className={`p-4 rounded-xl border ${
            isDarkMode ? 'bg-[#131B2C]/80 border-white/10' : 'bg-white/80 border-black/5 shadow-sm'
          }`}>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500">
                <Clock className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                  {stats.pending}
                </p>
                <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {language === 'ar' ? 'قيد الانتظار' : 'Pending'}
                </p>
              </div>
            </div>
          </div>
          
          <div className={`p-4 rounded-xl border ${
            isDarkMode ? 'bg-[#131B2C]/80 border-white/10' : 'bg-white/80 border-black/5 shadow-sm'
          }`}>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-gradient-to-r from-green-500 to-emerald-500">
                <CheckCircle className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                  {stats.completed}
                </p>
                <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {language === 'ar' ? 'مكتمل' : 'Completed'}
                </p>
              </div>
            </div>
          </div>
          
          <div className={`p-4 rounded-xl border ${
            isDarkMode ? 'bg-[#131B2C]/80 border-white/10' : 'bg-white/80 border-black/5 shadow-sm'
          }`}>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-gradient-to-r from-red-500 to-red-600">
                <XCircle className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                  {stats.rejected}
                </p>
                <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {language === 'ar' ? 'مرفوض' : 'Rejected'}
                </p>
              </div>
            </div>
          </div>
          
          <div className={`p-4 rounded-xl border ${
            isDarkMode ? 'bg-[#131B2C]/80 border-white/10' : 'bg-white/80 border-black/5 shadow-sm'
          }`}>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg gradient-primary">
                <Wallet className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                  {formatPrice(stats.totalAmount)}
                </p>
                <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {language === 'ar' ? 'مبلغ الانتظار' : 'Pending Amount'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className={`flex flex-col sm:flex-row gap-3 mb-5 p-4 rounded-xl border ${
          isDarkMode ? 'bg-[#131B2C]/80 border-white/10' : 'bg-white/80 border-black/5 shadow-sm'
        }`}>
          <div className="relative flex-1">
            <Search className={`absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={language === 'ar' ? 'بحث بالاسم أو البريد...' : 'Search by name or email...'}
              className={`ps-10 ${isDarkMode ? 'bg-[#0F172A] border-[#334155] text-white' : ''}`}
            />
          </div>
          
          <div className="flex gap-2">
            {[
              { id: 'all', label: language === 'ar' ? 'الكل' : 'All' },
              { id: 'pending', label: language === 'ar' ? 'قيد الانتظار' : 'Pending' },
              { id: 'completed', label: language === 'ar' ? 'مكتمل' : 'Completed' },
              { id: 'failed', label: language === 'ar' ? 'مرفوض' : 'Rejected' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setFilter(tab.id)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  filter === tab.id
                    ? 'gradient-primary text-white'
                    : isDarkMode
                      ? 'text-gray-400 hover:bg-white/5'
                      : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Requests List */}
        <div className={`rounded-xl overflow-hidden border ${
          isDarkMode ? 'bg-[#131B2C]/80 border-white/10' : 'bg-white/80 border-black/5 shadow-sm'
        }`}>
          {filteredRequests.length > 0 ? (
            <div className={`divide-y ${isDarkMode ? 'divide-white/10' : 'divide-gray-100'}`}>
              {filteredRequests.map((request) => (
                <div key={request.id} className={`p-4 transition-colors ${isDarkMode ? 'hover:bg-white/5' : 'hover:bg-gray-50'}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 flex-1">
                      <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-white/5' : 'bg-gray-100'}`}>
                        <User className="h-5 w-5 text-[#38BDF8]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                          {request.userName || 'Unknown User'}
                        </p>
                        <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} dir="ltr">
                          {request.userEmail}
                        </p>
                        <div className="flex items-center gap-2 mt-2">
                          <Calendar className={`h-3 w-3 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                          <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                            {new Date(request.createdAt).toLocaleDateString(language === 'ar' ? 'ar-AE' : 'en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                          {/* iter-224: back-date correction — admin only */}
                          <button
                            type="button"
                            onClick={() => handleEditDate(request)}
                            disabled={processingId === (request.id || request._id)}
                            className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-md border transition-colors ${
                              isDarkMode
                                ? 'text-amber-300 border-amber-500/40 bg-amber-500/10 hover:bg-amber-500/20'
                                : 'text-amber-700 border-amber-300 bg-amber-50 hover:bg-amber-100'
                            } disabled:opacity-40`}
                            data-testid={`edit-date-${request.reference_number || request.id}`}
                            title={language === 'ar' ? 'تعديل التاريخ' : 'Edit date'}
                          >
                            <CalendarCog className="h-3 w-3" />
                            {language === 'ar' ? 'تعديل' : 'Edit'}
                          </button>
                        </div>
                        {(request.reference_number || request.referenceNumber) && (
                          <p className={`text-[10px] font-mono mt-1 ${isDarkMode ? 'text-white/40' : 'text-slate-500'}`} dir="ltr">
                            {request.reference_number || request.referenceNumber}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <div className="text-end">
                      <p className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                        {formatPrice(request.amount)}
                      </p>
                      <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {request.currency}
                      </p>
                      <div className="mt-2">
                        {getStatusBadge(request.status)}
                      </div>
                    </div>
                  </div>
                  
                  {/* Bank Details */}
                  {request.bankDetails && (
                    <div className={`mt-3 p-3 rounded-lg ${isDarkMode ? 'bg-white/5' : 'bg-gray-50'}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <Building2 className="h-4 w-4 text-emerald-500" />
                        <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                          {request.bankDetails.bankName}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div>
                          <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>
                            {language === 'ar' ? 'صاحب الحساب:' : 'Account Holder:'}
                          </span>
                          <span className={`ms-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            {request.bankDetails.accountHolderName}
                          </span>
                        </div>
                        <div>
                          <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>
                            {language === 'ar' ? 'رقم الحساب:' : 'Account:'}
                          </span>
                          <span className={`ms-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`} dir="ltr">
                            {request.bankDetails.accountNumber}
                          </span>
                        </div>
                        <div className="col-span-2">
                          <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>IBAN:</span>
                          <span className={`ms-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`} dir="ltr">
                            {request.bankDetails.iban}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Actions */}
                  {request.status === 'pending' && (
                    <div className="flex gap-2 mt-4">
                      <Button
                        onClick={() => handleProcessRequest(request.id, 'approve')}
                        disabled={processingId === request.id}
                        className="flex-1 bg-gradient-to-r from-green-500 to-emerald-500 hover:opacity-90 text-white"
                      >
                        {processingId === request.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <>
                            <CheckCircle className="h-4 w-4 me-2" />
                            {language === 'ar' ? 'موافقة' : 'Approve'}
                          </>
                        )}
                      </Button>
                      <Button
                        onClick={() => handleProcessRequest(request.id, 'reject')}
                        disabled={processingId === request.id}
                        variant="outline"
                        className={`flex-1 border-red-500 text-red-500 hover:bg-red-500/10 ${isDarkMode ? '' : ''}`}
                      >
                        <XCircle className="h-4 w-4 me-2" />
                        {language === 'ar' ? 'رفض' : 'Reject'}
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="py-16 text-center">
              <Wallet className={`h-16 w-16 mx-auto mb-4 ${isDarkMode ? 'text-gray-600' : 'text-gray-300'}`} />
              <p className={`text-lg font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                {language === 'ar' ? 'لا توجد طلبات' : 'No requests found'}
              </p>
              <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                {language === 'ar' ? 'ستظهر طلبات السحب هنا' : 'Withdrawal requests will appear here'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
